'use client'

import { useVeritonic } from '@nwwp/veritonic'
import { GaugeSVG } from './GaugeSVG'
import type { CalculatorState } from './types'
import { calculatorLeadValues, ONTRACK_UNLOCK_MAGNET } from './unlock-magnet'
import { type CreateLeadMagnetLeadFn, LeadCaptureForm } from '../../../lead-magnets/components/LeadCaptureForm'

function LockGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden="true"
    >
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" strokeLinecap="round" />
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8.5l3.2 3L13 4.5" />
    </svg>
  )
}

/** Fixed decoy fill — labels are blurred and the arc stays one color, so it can't be read as a result. */
const PREVIEW_RATIO = 0.72

/**
 * Generic sample curve. Fixed shape, no relation to the visitor's inputs — their real projection
 * must never be in the DOM before unlock. Ends at the "need" line so it reads as neutral.
 */
const SAMPLE_CURVE = 'M0 196 C 110 190, 190 176, 270 150 C 350 124, 430 94, 520 80 C 560 74, 585 72, 600 72'
const SAMPLE_AREA = `${SAMPLE_CURVE} L600 220 L0 220 Z`
const SAMPLE_NEED_Y = 72

const PREVIEW_ITEMS = [
  'Whether you’re on track, and by how much',
  'What you’ll have at retirement vs. what you’ll need',
  'Your projected savings each year through retirement',
] as const

interface LockedPreviewProps {
  state: Pick<CalculatorState, 'age' | 'retirementAge'>
}

export function LockedPreview({ state }: LockedPreviewProps) {
  return (
    <section
      aria-labelledby="ontrack-preview-heading"
      className="dark overflow-hidden rounded-3xl bg-green-darker text-white"
    >
      <div className="flex items-start justify-between gap-4 px-5 pt-5 tablet:px-8 tablet:pt-8">
        <h2 id="ontrack-preview-heading" className="text-title-xs text-white">
          Your retirement picture at {state.retirementAge}
        </h2>
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-body-xs font-bold whitespace-nowrap text-white">
          <LockGlyph />
          Ready to unlock
        </span>
      </div>

      <GaugeSVG
        ratio={PREVIEW_RATIO}
        obscureLabels
        className="mx-auto mt-2 w-full max-w-40 tablet:mt-4 tablet:max-w-52"
      />

      <figure className="mt-2 hidden px-8 tablet:block">
        <figcaption className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-body-xs text-green-lighter-2">
          <span>
            Your savings path, age {state.age} to {state.retirementAge}
          </span>
          <span className="flex items-center gap-4" aria-hidden="true">
            <span className="flex items-center gap-2">
              <span className="h-1 w-5 rounded-full bg-green-lighter-2" />
              Projected savings
            </span>
            <span className="flex items-center gap-2">
              <span className="h-0 w-5 border-t-2 border-dashed border-bone" />
              What you&apos;ll need
            </span>
          </span>
        </figcaption>

        <div className="relative mt-3 select-none" aria-hidden="true">
          <svg viewBox="0 0 600 220" className="h-40 w-full tablet:h-56" preserveAspectRatio="none">
            <defs>
              <filter id="ontrack-preview-blur" x="-5%" y="-20%" width="110%" height="140%">
                <feGaussianBlur stdDeviation="3.5" />
              </filter>
              <linearGradient id="ontrack-preview-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#b7ecda" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#b7ecda" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {[52, 96, 140, 184].map((y) => (
              <line key={y} x1="0" x2="600" y1={y} y2={y} stroke="#ffffff" strokeOpacity="0.08" />
            ))}
            <g filter="url(#ontrack-preview-blur)">
              <path d={SAMPLE_AREA} fill="url(#ontrack-preview-fill)" />
              <path d={SAMPLE_CURVE} fill="none" stroke="#b7ecda" strokeWidth="4" strokeLinecap="round" />
              <line
                x1="0"
                x2="600"
                y1={SAMPLE_NEED_Y}
                y2={SAMPLE_NEED_Y}
                stroke="#f4eee6"
                strokeWidth="2.5"
                strokeDasharray="10 8"
              />
              <circle cx="600" cy={SAMPLE_NEED_Y} r="9" fill="#b7ecda" stroke="#154238" strokeWidth="4" />
            </g>
          </svg>
          <div className="mt-2 flex justify-between text-body-xs text-bone tabular-nums">
            <span>Age {state.age}</span>
            <span>Age {state.retirementAge}</span>
          </div>
        </div>
      </figure>

      <div className="mt-6 border-t border-white/12 p-5 tablet:px-8 tablet:py-6">
        <h3 className="text-body-xs font-bold text-green-lighter-2 uppercase">What you&apos;ll see</h3>
        <ul className="mt-3 flex flex-col gap-2.5">
          {PREVIEW_ITEMS.map((item) => (
            <li key={item} className="flex items-start gap-3 text-body-sm text-bone">
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-lighter-2/15 text-green-lighter-2">
                <CheckGlyph />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

interface OnTrackUnlockFormProps {
  createLead: CreateLeadMagnetLeadFn
  state: CalculatorState
}

export function OnTrackUnlockForm({ createLead, state }: OnTrackUnlockFormProps) {
  const { trackFlowStart } = useVeritonic()

  return (
    <div className="flex flex-col gap-6 border-t border-smoke pt-7">
      <h3 id="ontrack-unlock-heading" className="text-body-bold text-charcoal">
        {ONTRACK_UNLOCK_MAGNET.headline}
      </h3>
      <LeadCaptureForm
        createLead={createLead}
        magnet={ONTRACK_UNLOCK_MAGNET}
        knownValues={calculatorLeadValues(state)}
        onFlowStart={() => {
          try {
            trackFlowStart()
          } catch (e) {
            console.warn('trackFlowStart failed', { error: e })
          }
        }}
      />
    </div>
  )
}
